import { useEffect, useState } from 'react';
import { Loader2, Activity, AlertTriangle, Pill, Scissors, History } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_TONE = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
};

function Badges({ items, tone, testidPrefix }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-slate-400 italic">None recorded.</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((t, i) => (
        <span key={`${testidPrefix}-${i}`} data-testid={`${testidPrefix}-${i}`}
          className={`text-xs px-2.5 py-1 rounded-full border font-medium ${tone}`}>{t}</span>
      ))}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-2">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      {children}
    </div>
  );
}

function GrayPill() {
  return (
    <div className="inline-flex items-center gap-2 text-xs font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-3 py-1.5"
      data-testid="longitudinal-brief-pill">
      <Activity className="w-3.5 h-3.5" />
      Single visit — longitudinal brief available after second linked encounter.
    </div>
  );
}

export default function LongitudinalBrief({ result }) {
  const patientId = result?.patient_id || null;
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    let active = true;
    setLoading(true);
    fetch(`${API}/patient/${patientId}/longitudinal-brief`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active) { setBrief(d); setLoading(false); } })
      .catch(() => { if (active) { setLoading(false); } });
    return () => { active = false; };
  }, [patientId]);

  if (!patientId) return <GrayPill />;
  if (loading || !brief) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="longitudinal-brief-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading longitudinal brief…
      </div>
    );
  }
  if ((brief.encounter_count || 0) < 2) return <GrayPill />;

  const dr = brief.date_range || {};
  return (
    <div className="space-y-1" data-testid="longitudinal-brief">
      <div className="text-xs text-slate-500 mb-4">
        <strong className="text-slate-700">{brief.encounter_count}</strong> linked visits
        {dr.first && dr.last && (
          <> · {(dr.first || '').slice(0, 10)} → {(dr.last || '').slice(0, 10)}</>
        )}
        <span className="ml-1 italic">· deterministic 2-page summary across all visits</span>
      </div>

      <Section icon={Activity} title="Active conditions">
        <Badges items={brief.active_conditions} tone="bg-sky-100 text-sky-800 border-sky-200" testidPrefix="lb-active" />
      </Section>

      <Section icon={History} title="Chronic conditions">
        <Badges items={brief.chronic_conditions} tone="bg-violet-100 text-violet-800 border-violet-200" testidPrefix="lb-chronic" />
      </Section>

      <Section icon={Scissors} title="Surgical history (chronological)">
        {brief.surgical_history?.length > 0 ? (
          <ul className="border-l-2 border-slate-200 pl-4 space-y-2" data-testid="lb-surgical-timeline">
            {brief.surgical_history.map((s, i) => (
              <li key={`lb-sx-${i}`} data-testid={`lb-sx-${i}`} className="text-sm text-slate-700 relative">
                <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-slate-400" />
                <strong>{s.year || '—'}</strong> · {s.procedure}
                {s.facility && <span className="text-slate-500"> · {s.facility}</span>}
                {s.complication && <span className="text-red-600 font-semibold"> · {s.complication}</span>}
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-slate-400 italic">None recorded.</p>}
      </Section>

      <Section icon={Pill} title="Medications (deduplicated · first seen)">
        {brief.medications?.length > 0 ? (
          <table className="w-full text-sm" data-testid="lb-medications-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="py-1.5 pr-3">Medication</th>
                <th className="py-1.5">First seen</th>
              </tr>
            </thead>
            <tbody>
              {brief.medications.map((m, i) => (
                <tr key={`lb-med-${i}`} data-testid={`lb-med-${i}`} className="border-b border-slate-100">
                  <td className="py-1.5 pr-3 text-slate-700">{m.name}</td>
                  <td className="py-1.5 text-slate-500 text-xs">{(m.first_seen || '').slice(0, 10) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-sm text-slate-400 italic">None recorded.</p>}
      </Section>

      <Section icon={AlertTriangle} title="Red flags across all visits">
        {brief.red_flags?.length > 0 ? (
          <div className="space-y-2" data-testid="lb-red-flags">
            {brief.red_flags.map((f, i) => (
              <div key={`lb-rf-${i}`} data-testid={`lb-rf-${i}`} className="flex items-start gap-2 text-sm">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${SEV_TONE[f.severity] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{f.severity}</span>
                <div>
                  <span className="text-slate-800 font-medium">{f.category}</span>
                  <span className="text-slate-400 text-xs ml-1">· {(f.date || '').slice(0, 10)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400 italic">No HIGH/CRITICAL flags recorded.</p>}
      </Section>

      {brief.cross_system_triggers?.length > 0 && (
        <Section icon={AlertTriangle} title="Cross-system triggers (Engine 82 / 83)">
          <div className="space-y-2" data-testid="lb-cross-system">
            {brief.cross_system_triggers.map((c, i) => (
              <div key={`lb-cs-${i}`} data-testid={`lb-cs-${i}`}
                className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-sm text-amber-900">
                <strong>{c.category}</strong>
                <span className="text-amber-600 text-xs ml-1">· {(c.date || '').slice(0, 10)}</span>
                {c.triggers?.length > 0 && (
                  <div className="text-[11px] text-amber-700 mt-0.5">triggers: {c.triggers.join(' · ')}</div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {brief.worst_first_differential_trend?.length > 0 && (
        <Section icon={Activity} title="Worst-first differential trend">
          <ul className="space-y-1" data-testid="lb-diff-trend">
            {brief.worst_first_differential_trend.map((d, i) => (
              <li key={`lb-diff-${i}`} data-testid={`lb-diff-${i}`} className="text-sm text-slate-700">
                <span className="text-slate-400 text-xs">{(d.date || '').slice(0, 10)}</span> — {d.top_diagnosis}
                {d.urgency && <span className="text-[11px] text-slate-500 ml-1">({d.urgency})</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {brief.outstanding_gaps?.length > 0 && (
        <Section icon={AlertTriangle} title="Outstanding documentation gaps (all visits)">
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-0.5" data-testid="lb-gaps">
            {brief.outstanding_gaps.map((g, i) => (
              <li key={`lb-gap-${i}`} data-testid={`lb-gap-${i}`}>{g}</li>
            ))}
          </ul>
        </Section>
      )}

      <p className="text-[10px] text-slate-400 mt-2 border-t border-slate-100 pt-2">
        Deterministic aggregation from existing encounter data · no LLM · physician review required.
      </p>
    </div>
  );
}
